import { useState } from 'react';
import i18n from '@/utils/i18n';
import { FaCopy, FaLink, FaPlus, FaUnlink } from 'react-icons/fa';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { useGetAllEventLinkedUsersQuery } from '../../../graphql/queries/getAllEventLinkedUsers.generated';
import { useAddEventLinkedUserMutation } from '../../../graphql/mutations/addEventLinkedUser.generated';
import { Role } from '../../../graphql/generatedTypes';
import { useUnlinkUserFromEventMutation } from '../../../graphql/mutations/unlinkUserFromEvent.generated';
import { useLinkUserToEventMutation } from '../../../graphql/mutations/linkUserToEvent.generated';
import { EventLinkedUserDetailFragment } from '../../../graphql/fragments/eventLinkedUser.generated';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Card } from '@/components/ui/card';

type EventLinkedUsersTableTableProps = {
  eventId: string;
  loadingEventLinkedUsers: boolean;
  errorEventLinkedUsers: any;
  eventEventLinkedUsers: EventLinkedUserDetailFragment[];
  refetchEventLinkedUser: () => void;
};

export const EventLinkedUsersTable = ({
  eventId,
  loadingEventLinkedUsers,
  errorEventLinkedUsers,
  eventEventLinkedUsers,
  refetchEventLinkedUser,
}: EventLinkedUsersTableTableProps) => {
  const [showAddLinkedUserModal, setShowAddLinkedUserModal] = useState(false);
  const [showLinkedUserModal, setShowLinkedUserModal] = useState(false);

  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState<string>(
    Role.ExternalEventSupervisor,
  );
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: allEventLinkedUsersData } = useGetAllEventLinkedUsersQuery({
    variables: { filter: {} },
    fetchPolicy: 'no-cache',
  });

  const [addEventLinkedUser] = useAddEventLinkedUserMutation({
    onCompleted: () => refetchEventLinkedUser(),
    onError: (error) => {
      LayoutStoreInstance.setError(error.message);
    },
  });

  const [unlinkUserFromEvent] = useUnlinkUserFromEventMutation({
    onCompleted: () => refetchEventLinkedUser(),
    onError: (error) => {
      LayoutStoreInstance.setError(error.message);
    },
  });

  const [linkUserToEvent] = useLinkUserToEventMutation({
    onCompleted: () => refetchEventLinkedUser(),
    onError: (error) => {
      LayoutStoreInstance.setError(error.message);
    },
  });

  return (
    <>
      <h2 className="tw:mt-4 tw:text-2xl tw:font-semibold">
        {i18n.t('Event:linkedUsers')}
      </h2>

      <div className="tw:flex tw:justify-end tw:gap-2 tw:mb-2">
        <Button onClick={() => setShowLinkedUserModal(true)}>
          <FaLink />
          {i18n.t('Event:link')}
        </Button>
        <Button onClick={() => setShowAddLinkedUserModal(true)}>
          <FaPlus />
          {i18n.t('Common:add')}
        </Button>
      </div>

      {loadingEventLinkedUsers ? (
        <div className="tw:py-4 tw:text-center">
          <div
            className="tw:inline-block tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-solid tw:border-current tw:border-r-transparent tw:text-primary"
            role="status"
          >
            <span className="tw:sr-only">{i18n.t('Common:loading')}</span>
          </div>
        </div>
      ) : errorEventLinkedUsers ? (
        <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:text-red-700">
          {i18n.t('Common:error')}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:py-3 tw:pl-4">
                  {i18n.t('Common:name')}
                </TableHead>
                <TableHead>{i18n.t('Event:password')}</TableHead>
                <TableHead>{i18n.t('User:role')}</TableHead>
                <TableHead>{i18n.t('Event:unlink')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eventEventLinkedUsers.map((eventLinkedUsers) => (
                <TableRow key={eventLinkedUsers.id}>
                  <TableCell className="tw:pl-4">
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          eventLinkedUsers.user_name,
                        );
                        LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                      }}
                      size="icon"
                      className="tw:w-6 tw:h-6 tw:rounded-sm tw:mr-1"
                    >
                      <FaCopy className="tw:size-3" />
                    </Button>
                    <Link
                      to={Routes.users(eventLinkedUsers.id)}
                      className="tw:text-primary tw:hover:underline"
                    >
                      {eventLinkedUsers.user_name}
                    </Link>
                  </TableCell>

                  <TableCell>
                    <Button
                      size="icon"
                      className="tw:w-6 tw:h-6 tw:rounded-sm tw:mr-1"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          eventLinkedUsers.password || '',
                        );
                        LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                      }}
                    >
                      <FaCopy className="tw:size-3" />
                    </Button>
                    {eventLinkedUsers.password}
                  </TableCell>

                  <TableCell>
                    <span className="tw:whitespace-nowrap tw:bg-primary-dark tw:mr-1 tw:mb-1 tw:rounded tw:px-2 tw:py-1 tw:text-white tw:text-sm">
                      {i18n.t(`User:role_${eventLinkedUsers.role}`)}
                    </span>
                  </TableCell>

                  <TableCell
                    className="tw:cursor-pointer tw:pr-4"
                    onClick={() =>
                      unlinkUserFromEvent({
                        variables: {
                          event_id: eventId,
                          user_id: eventLinkedUsers.id,
                        },
                      })
                    }
                  >
                    <FaUnlink />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog
        open={showAddLinkedUserModal}
        onOpenChange={setShowAddLinkedUserModal}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n.t('Event:addEventLinkedUser')}</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>

          <FieldGroup>
            <Field>
              <FieldLabel>{i18n.t('Common:userName')}</FieldLabel>
              <Input
                type="text"
                onChange={(e) => setNewUserName(e.target.value)}
                value={newUserName}
              />
            </Field>

            <Field>
              <FieldLabel>{i18n.t('User:selectRole')}</FieldLabel>
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={Role.ExternalEventSupervisor}>
                    {i18n.t(`User:role_${Role.ExternalEventSupervisor}`)}
                  </SelectItem>
                  <SelectItem value={Role.EventOwner}>
                    {i18n.t(`User:role_${Role.EventOwner}`)}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </FieldGroup>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setNewUserName('');
                setNewUserRole(Role.ExternalEventSupervisor);
                setShowAddLinkedUserModal(false);
              }}
            >
              {i18n.t('Common:abort')}
            </Button>
            <Button
              onClick={() => {
                addEventLinkedUser({
                  variables: {
                    payload: {
                      event_id: eventId,
                      user_name: newUserName,
                      role: newUserRole,
                    },
                  },
                });
                setNewUserName('');
                setNewUserRole(Role.ExternalEventSupervisor);
                setShowAddLinkedUserModal(false);
              }}
            >
              {i18n.t('Common:confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showLinkedUserModal} onOpenChange={setShowLinkedUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n.t('Event:linkEventUser')}</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel>{i18n.t('Event:user')}</FieldLabel>
              <select
                className="tw:w-full tw:min-h-0 tw:border-input tw:border tw:p-2"
                onChange={(e) => setSelectedUserId(e.target.value)}
                value={selectedUserId || ''}
              >
                <option value="" disabled>
                  {i18n.t('Event:selectALinkableUser')}
                </option>
                {allEventLinkedUsersData?.getAllEventLinkedUsers
                  .filter(
                    (s) => !eventEventLinkedUsers.some((s2) => s.id === s2.id),
                  )
                  .map((eventLinkedUser) => (
                    <option key={eventLinkedUser.id} value={eventLinkedUser.id}>
                      {eventLinkedUser.user_name}
                    </option>
                  ))}
              </select>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedUserId(null);
                setShowLinkedUserModal(false);
              }}
            >
              {i18n.t('Common:abort')}
            </Button>
            <Button
              disabled={!selectedUserId}
              onClick={() => {
                linkUserToEvent({
                  variables: {
                    event_id: eventId,
                    user_id: selectedUserId || '',
                  },
                });
                setSelectedUserId(null);
                setShowLinkedUserModal(false);
              }}
            >
              {i18n.t('Common:confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
